import React from "react";
import ReactDOM from "react-dom";
import cx from "classnames";

// BottomSheet에서 쓰는 버튼그룹 그대로 재사용
import {
  SingleButton,
  TwinColumn,
  SplitRowHalf,
  SplitRowQuarter,
} from "./ButtonGroup";

// BottomSheet와 동일한 키로 매핑
const BUTTON_COMPONENTS = {
  single: SingleButton,
  columm: TwinColumn,
  half: SplitRowHalf,
  quarter: SplitRowQuarter,
};

/**
 * 공통 팝업 컴포넌트
 *
 * - isOpen: true일 때만 렌더링
 * - title, subtitle: 상단 텍스트
 * - children: 가운데 컨텐츠 (점선 박스 / 폼 등)
 *
 * - renderButtons:
 *    버튼 영역 전체를 직접 렌더링하고 싶을 때 사용
 *    ex) renderButtons={<SplitRowHalf ... />}
 *
 * - buttonVariant:
 *    "single" | "columm" | "half" | "quarter"
 *    => ButtonGroup에서 해당 컴포넌트 자동 선택
 *
 * - buttonProps:
 *    선택된 버튼 컴포넌트에 그대로 전달할 props
 *    (label, onClick, disabled, leftLabel, rightLabel 등 자유롭게)
 */
const Popup = ({
  isOpen,
  onClose,

  title = "타이틀을 입력해주세요",
  subtitle = "",
  children,

  // 버튼 관련
  renderButtons,          // JSX로 직접 버튼 넣고 싶을 때
  buttonVariant = "single",
  buttonProps = {},

  // 스타일 커스터마이즈
  className,
  contentClassName,
}) => {
  const [keyboardInset, setKeyboardInset] = React.useState(0);

  React.useEffect(() => {
    if (!isOpen || typeof window === "undefined") return undefined;

    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const updateKeyboardInset = () => {
      const nextInset = Math.max(
        0,
        Math.round(window.innerHeight - (viewport.height + viewport.offsetTop))
      );
      setKeyboardInset(nextInset > 80 ? nextInset : 0);
    };

    updateKeyboardInset();
    viewport.addEventListener("resize", updateKeyboardInset);
    viewport.addEventListener("scroll", updateKeyboardInset);
    window.addEventListener("resize", updateKeyboardInset);

    return () => {
      viewport.removeEventListener("resize", updateKeyboardInset);
      viewport.removeEventListener("scroll", updateKeyboardInset);
      window.removeEventListener("resize", updateKeyboardInset);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const ButtonComp = BUTTON_COMPONENTS[buttonVariant] || SingleButton;

  const body = (
    <div
      className={cx(
        "fixed inset-0 z-[1000]",
        "flex items-center justify-center",
        "bg-surface-dark-absolute-dimmed_60"
      )}
      style={{
        padding: "16px",
        paddingBottom: `${16 + keyboardInset}px`,
        alignItems: keyboardInset > 0 ? "flex-end" : "center",
      }}
      onClick={handleBackdropClick}
    >
      <div
        className={cx(
          "w-[312px] p-[8px] rounded-[24px] ",
          "bg-surface-light-white-layered",
          className
        )}
        style={{
          maxHeight: `calc(100vh - ${Math.max(32, keyboardInset + 32)}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-[16px] pb-[8px]">
          {/* 헤더 */}
          <h2 className="text-t3 text-text-default pb-[8px]">
            {title}
          </h2>
          {subtitle && (
            <p className="text-b2 text-text-subtler whitespace-pre-line">
              {subtitle}
            </p>
          )}

          {/* 컨텐츠 영역 */}
          {children && (
            <div
              className={cx(
                "max-h-[50vh] overflow-y-auto pt-[24px]",
                contentClassName
              )}
            >
              {children}
            </div>
          )}
        </div>

        {/* 버튼 영역 */}
        <div className="">
          {renderButtons ? (
            // JSX 그대로 렌더
            renderButtons
          ) : (
            // buttonVariant + buttonProps 사용
            <ButtonComp {...buttonProps} />
          )}
        </div>
      </div>
    </div>
  );

  // BottomSheet처럼 Portal로 띄우기
  return ReactDOM.createPortal(body, document.body);
};

export default Popup;
